/**
 * groupRecords (Unit 5 Phase 3) — the records-grid bucketer.
 *
 * PURE, like sortRecords and filterRecords. No React, no Dexie, no storage.
 * Takes the host's ALREADY-SORTED array plus a sanitized { primary, secondary }
 * and returns a bucket tree; the input is never mutated.
 *
 * IT RECEIVES THE SORTED ARRAY AND MUST NOT REORDER IT. The host runs filter,
 * then sort, then this. Row order inside a bucket is the order the rows arrived
 * in, full stop — the rep's sort is the only thing that decides it, and a second
 * ordering opinion here would mean the grid disagreed with the Sort sheet about
 * the same rows.
 *
 * VALUES COME FROM record.working[key], NEVER record[key] — the same rule the
 * comparator and the filter evaluator state, and the same access the grid's own
 * cell renderer uses. The row document carries identity and bookkeeping at the
 * top level and every FIELD VALUE one level down in `working`; bucketing on
 * record[key] would find one key and undefined for all the others, collapsing
 * every row into a single blank bucket while the UI reported a grouping.
 *
 * ⚠ A STRUCTURE, NOT A FLAT ARRAY WITH HEADER SENTINELS. Bob's prototype
 * splices pseudo-rows carrying an _isGroupHeader flag into the row array and
 * lets the renderer branch on it. We must not: the row component is SHARED with
 * the search overlay and keys on record_id, which a header does not have, so a
 * sentinel row would either collide on an undefined key or force the shared
 * component to learn about grouping. Returning a tree keeps the row component
 * exactly as dumb as it is today and leaves the header chrome entirely to the
 * caller that wants it.
 *
 * BLANKS BUCKET TOGETHER AND SORT LAST. A value failing the shared emptiness
 * test lands in one bucket under the literal BLANK_BUCKET label, and that
 * bucket is forced to the end at BOTH levels regardless of how it would
 * collate. This is the grid's empty-sorts-last rule expressed one more time: a
 * rep who sorts blanks to the bottom and then groups must not find them back at
 * the top under a bracket. `false` and `0` are values, not blanks, and bucket
 * under their own labels.
 *
 * BUCKET ORDER IS THE SHARED COLLATOR — natural, case-insensitive, one pinned
 * locale — so a bucket list reads in the same order as the rows inside it and
 * as the filter's own pick lists. TOOL-2 leads TOOL-10.
 */

import { isEmpty, TEXT_COLLATOR } from './fieldValueRules';

/**
 * The label every empty value buckets under.
 *
 * Exported so the renderer names the blank bucket by importing this rather than
 * restating the literal. Two copies of a user-visible sentinel is how one of
 * them eventually says something slightly different, and it would also make the
 * "force this bucket last" rule below untestable from the outside.
 */
export const BLANK_BUCKET = '(blank)';

/**
 * One record's bucket label for one field. Guarded read, not optional chaining,
 * matching the grid's own cell read: a row with no `working` object at all is a
 * blank, not a throw.
 */
function bucketLabel(record, fieldKey) {
  const raw = record.working ? record.working[fieldKey] : undefined;
  return isEmpty(raw) ? BLANK_BUCKET : String(raw);
}

/**
 * Bucket-label order: the shared collator, EXCEPT that the blank bucket is
 * pinned last.
 *
 * The blank test runs BEFORE the collator is consulted, which is what makes
 * "last regardless of collation" true — under plain collation a literal
 * '(blank)' sorts among the punctuation at the very front, which is precisely
 * the position the empty-sorts-last rule exists to prevent.
 */
function compareBuckets(a, b) {
  if (a === b) return 0;
  if (a === BLANK_BUCKET) return 1;
  if (b === BLANK_BUCKET) return -1;
  return TEXT_COLLATOR.compare(a, b);
}

/**
 * One level of bucketing over a row list.
 *
 * A Map accumulates in first-seen order and the keys are then sorted
 * explicitly, so the output order is a property of compareBuckets and never of
 * how the rows happened to arrive. Rows are appended in input order and never
 * touched again, which is the no-reordering contract.
 */
function bucketize(rows, fieldKey) {
  const byLabel = new Map();
  for (const record of rows) {
    const label = bucketLabel(record, fieldKey);
    const bucket = byLabel.get(label);
    if (bucket) bucket.push(record);
    else byLabel.set(label, [record]);
  }
  return [...byLabel.keys()]
    .sort(compareBuckets)
    .map((value) => ({ value, rows: byLabel.get(value) }));
}

/**
 * Apply a grouping to an already-sorted records array.
 *
 * @param {Array} records  the host's sorted rows (never mutated)
 * @param {Object} group   sanitized { primary, secondary } — string keys or null
 * @returns {Array} the bucket tree, or `records` ITSELF, by reference, when
 *   there is no grouping to apply. That identity is load-bearing and is the
 *   same contract sortRecords and filterRecords hold: the host wraps this in a
 *   useMemo, so an ungrouped grid gets the exact array it already had and React
 *   re-renders nothing.
 *
 * The returned shape, one entry per bucket:
 *
 *   { value, count, rows, children }
 *
 *   value     the bucket label — a field value as a string, or BLANK_BUCKET
 *   count     ROWS beneath this bucket, never the number of child buckets. At
 *             the outer level of a two-level grouping this is still the row
 *             total, because "TOOL TYPE: DIE — 34" has to mean 34 tools; a rep
 *             reading a count of child buckets there would be told the site
 *             holds four of something it holds thirty-four of.
 *   rows      the bucket's rows in input order, or null when children is set
 *   children  the same shape one level down, or null at a leaf
 *
 * `rows` and `children` are mutually exclusive by construction, so a consumer
 * never has to decide which of two populated fields to render.
 */
export default function groupRecords(records, group) {
  if (!Array.isArray(records)) return records;
  if (!group || typeof group !== 'object') return records;

  const { primary, secondary } = group;
  if (typeof primary !== 'string' || primary === '') return records;

  // Defence in depth, mirroring the sort and filter modules. The host's value
  // arrives sanitized from useFieldGroupState, but this module is exported and
  // pure — a caller that hands it junk should get its rows back ungrouped
  // rather than a throw inside a bucketer.
  const inner = (
    typeof secondary === 'string' && secondary !== '' && secondary !== primary
  ) ? secondary : null;

  return bucketize(records, primary).map(({ value, rows }) => {
    if (inner === null) {
      return { value, count: rows.length, rows, children: null };
    }
    const children = bucketize(rows, inner).map((child) => ({
      value: child.value,
      count: child.rows.length,
      rows: child.rows,
      children: null,
    }));
    // count stays the ROW total, not children.length — see the docblock above.
    return { value, count: rows.length, rows: null, children };
  });
}
